/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * FormSelect.tsx
 */

import HiloReactComponent from "../layout/HiloReactComponent";
import React from "react";
import Colors from "../../model/enumerations/Colors";
import {FormElementProps} from "./FormElement";
import {Selectable} from "../../model/form/Selectable";
import {SelectField} from "../../model/form/SelectField";

interface State<D> {
    selectedItems: Array<Selectable<D>>;
}

export interface Props<D> extends FormElementProps<D> {
    field: SelectField<D>
}

export default class FormSelect<D> extends HiloReactComponent<Props<D>, State<D>> {
    state: State<D> = {selectedItems: []};

    private _loading: Boolean = false;

    /**
     * componentDidMount
     */
    public componentDidMount() {
        super.componentDidMount();

        this._restoreSelectedValues();
    }

    /**
     * _restoreSelectedValues
     * @private
     */
    private _restoreSelectedValues = async () => {
        const {field} = this.props;

        if (this._loading) return;

        this._loading = true;
        let selectedItems = [];
        if (field.value instanceof Array) {
            for (let val of field.value) {
                for (let option of field.options) {
                    if (option.value === val) selectedItems.push(option);
                }
            }
        } else {
            for (let option of field.options) {
                if (option.value === field.value) selectedItems.push(option);
            }
        }

        await this.setState({selectedItems: selectedItems})
        this._loading = false;
    };

    /**
     * componentDidUpdate
     * @param prevProps
     * @param prevState
     * @param snapshot
     */
    public componentDidUpdate(prevProps: Readonly<Props<D>>, prevState: Readonly<State<D>>, snapshot?: any): void {
        super.componentDidUpdate?.(prevProps, prevState, snapshot);

        const value = this.props.field.value;

        if (value instanceof Array && value.length !== this.state.selectedItems.length) {
            console.debug("componentDidUpdate.restoreSelectedValues", value.length, this.state.selectedItems.length);
            this._restoreSelectedValues();
        } else if (!(value instanceof Array)) {
            console.log("restoring selected values")
            this._restoreSelectedValues();
        }
    }

    /**
     * _selectItem
     * @param item
     * @private
     */
    private _selectItem = async (item: Selectable<D>) => {
        const {multiple, name} = this.props.field;

        if (multiple) this._multipleSelectItem(name, item);
        else this._singleSelectItem(name, item);
    };

    /**
     * _singleSelectItem
     * @param name
     * @param item
     * @private
     */
    private _singleSelectItem = (name: string, item: Selectable<D>) => {
        this.props.onChange?.(name, item.value);
        this.setState({selectedItems: [item]});
    };

    /**
     * _multipleSelectItem
     * @param name
     * @param item
     * @private
     */
    private _multipleSelectItem = (name: string, item: Selectable<D>) => {
        let {selectedItems} = this.state;

        const index = selectedItems.findIndex(value => value.name === item.name);

        if (index >= 0) selectedItems.splice(index, 1);
        else selectedItems.push(item);

        this.props.onChange?.(name, selectedItems.map(selectable => selectable.value));
        this.setState({selectedItems: [item]});
    }

    /**
     * render
     * @returns {any}
     */
    public render = () => {
        const {field} = this.props;
        const {options, title} = field;
        const {selectedItems} = this.state;

        return <div className={"field"}>
            <span>{title(this.t)}</span>
            <div className="form-select">
                {
                    options.length > 0 &&
                    options.map(item => {
                        const isSelected = selectedItems.findIndex(value => value.name === item.name) >= 0;

                        return <div key={item.name}
                                    className={"select-item no-select"}
                                    onClick={() => this._selectItem(item)}
                                    style={{
                                        borderColor: Colors.blue.bg,
                                        color: isSelected ? Colors.blue.fg : Colors.blue.bg,
                                        backgroundColor: isSelected ? Colors.blue.bg : Colors.blue.fg
                                    }}>
                            <p>{item.name}</p>
                        </div>
                    })
                }
                {
                    options.length === 0 && <p>{field.emptyLabel(this.t)}</p>
                }
            </div>
        </div>;
    }
}